// Cross-product + cross-tab logout synchronisation (no ticket — follow-up to CC-602).
//
// Three products share one Cognito identity across three origins:
//   Crime Central  (app*.crime-central.com)  — REACT_APP_CC_URL
//   Policy Central (policy*.crime-central.com) — this app
//   Platform       (www*.crime-central.com)   — REACT_APP_PLATFORM_URL
//
// Signing out of one must sign the user out of the others. This module provides
// both halves of the mechanism:
//   1. Same-origin, multi-tab sync via BroadcastChannel('pc-auth-logout').
//   2. Cross-origin sync: a hidden-iframe fan-out that loads each sibling's
//      /logout-bridge and postMessages it, plus this app's own bridge handler
//      (see pages/LogoutBridge.js) that receives a sibling's postMessage.
//
// Shared contract (identical in cc-app-web and platform-web):
//   - logout payload, sender -> bridge: { source: 'cc-platform-logout', v: 1,
//     type: 'logout' }, sent with an explicit targetOrigin (never '*').
//   - ready ack, bridge -> parent: { source: 'cc-platform-logout', v: 1,
//     type: 'ready' }. Sent with targetOrigin '*' — intentional, and only for
//     this ack: it carries no sensitive data, and the bridge iframe can't
//     reliably know its parent's origin.
//   - the bridge validates event.origin against a per-env sibling allowlist and
//     event.data.source before doing anything with a 'logout' message.
//
// Ready handshake (why): the bridge page only attaches its message listener
// inside a React effect, which runs on mount — after the iframe's `load` event
// has already fired. A one-shot postMessage sent from the sender's `load`
// handler races that effect and is routinely dropped. Instead, the sender
// waits for the bridge to announce readiness before sending the logout
// payload, with a short retry interval as a fallback for when the ready ack
// itself is missed (e.g. iframe blocked, message dropped, unusually slow load).

import * as cognitoAuth from './cognitoAuth';

// PC-specific channel name so it can't collide with CC's own 'cc-auth-logout'.
export const AUTH_LOGOUT_CHANNEL = 'pc-auth-logout';
// Deliberately product-neutral (not 'pc-...') — cc-app-web and platform-web
// send/expect this exact same marker string; it identifies the cross-product
// contract, not this specific app.
export const LOGOUT_MESSAGE_SOURCE = 'cc-platform-logout';
export const LOGOUT_MESSAGE = { source: LOGOUT_MESSAGE_SOURCE, v: 1, type: 'logout' };
export const READY_MESSAGE = { source: LOGOUT_MESSAGE_SOURCE, v: 1, type: 'ready' };
export const BRIDGE_PATH = '/logout-bridge';
// The sending tab unloads its fan-out iframes when it redirects (see
// CROSS_ORIGIN_LOGOUT_REDIRECT_DELAY_MS below) — that caps how long an iframe
// can actually live regardless of this value. Extended from 1500 to 2200 so
// the full retry window below (up to ~2000ms) has room to complete before
// teardown, as long as the redirect delay itself is also long enough.
const IFRAME_LIFETIME_MS = 2200;
// Fallback-retry cadence/window for when the bridge's ready ack is missed.
// In the no-ack path this tab still navigates away at
// CROSS_ORIGIN_LOGOUT_REDIRECT_DELAY_MS (1500ms), so in practice only the
// retries scheduled before that fire — this window is a ceiling for how long
// retrying continues if the tab is somehow kept alive longer (e.g. logout()
// called from a context without a redirect), not a guarantee of 8 attempts.
const READY_RETRY_INTERVAL_MS = 250;
const READY_RETRY_WINDOW_MS = 2000;
// The sender tears the fan-out iframe down at IFRAME_LIFETIME_MS, which aborts
// whatever the sibling's /logout-bridge page was still doing inside it — so the
// bridge's own best-effort revoke fetch must give up well before that, or it
// gets silently cut off mid-request instead of completing or failing cleanly.
const REVOKE_FETCH_TIMEOUT_MS = 1000;
// How long logout() waits after starting the fan-out before navigating this tab
// away. This has to cover the full ready round-trip — iframe load, ready ack
// back to the parent, logout postMessage back to the iframe, and the bridge's
// own revoke fetch — not just "the iframe loaded". 600ms was long enough for
// a `load`-only fire-and-forget post but is not reliably enough time for that
// round trip plus a couple of retry attempts, so this is raised to 1500ms
// (kept <= IFRAME_LIFETIME_MS so the iframe is still alive for the whole
// window).
export const CROSS_ORIGIN_LOGOUT_REDIRECT_DELAY_MS = 1500;

function toOrigin(url) {
  if (!url || url === 'undefined') return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

// The two sibling product origins for the CURRENT environment, derived from the
// same env vars AppSwitcher uses (populated per-env from SSM at build time).
// Used both as the fan-out targets and as the bridge's inbound origin allowlist.
export function getSiblingOrigins() {
  return [
    toOrigin(process.env.REACT_APP_CC_URL),
    toOrigin(process.env.REACT_APP_PLATFORM_URL),
  ].filter(Boolean);
}

// Strict validation of an inbound bridge postMessage. No wildcard matching:
// the sender origin must be one of this env's known siblings and the payload
// must carry the agreed source marker. A 'ready' ack is a distinct message on
// the same channel, not a logout instruction, so it's ignored here; an absent
// type is accepted for backward compatibility with the pre-handshake payload.
export function isValidLogoutMessage(event, allowedOrigins) {
  if (!event || !Array.isArray(allowedOrigins) || allowedOrigins.length === 0) return false;
  if (!allowedOrigins.includes(event.origin)) return false;
  if (!event.data || event.data.source !== LOGOUT_MESSAGE_SOURCE) return false;
  // v has been 1 since before this handshake existed — reject a mismatched
  // version outright rather than silently accepting a payload shape this
  // build doesn't know how to interpret.
  if (event.data.v !== 1) return false;
  const { type } = event.data;
  return type === undefined || type === 'logout';
}

// Post a one-shot logout notice on this app's own channel. Used by the bridge
// (a fresh iframe page with no long-lived channel of its own). The channel is
// closed on the next macrotask so the message is dispatched to other tabs
// before teardown, without leaking an open handle.
export function broadcastLogout() {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
    channel.postMessage({ type: 'logout' });
    setTimeout(() => {
      try {
        channel.close();
      } catch {
        /* already closed */
      }
    }, 0);
  } catch {
    /* best-effort — never block logout on a broadcast failure */
  }
}

// Best-effort server-side revoke + local SDK clear WITHOUT the full logout()
// flow (which would re-trigger the cross-origin fan-out and could loop).
// Runs inside a freshly-loaded /logout-bridge page: for a native Cognito login
// the SDK persists a session it can read and revoke here; for a platform-SSO
// session there is nothing readable (in-memory only, gone on reload).
//
// The cookie-clearing call is unconditional: a cold bridge iframe never has a
// readable SDK session, but the shared platform_refresh_token cookie still
// needs clearing regardless — gating it on idToken meant a platform-SSO
// logout never cleared that cookie at all. This mirrors cc-app-web's bridge,
// which always issues DELETE /auth/session.
export async function serverRevokeAndClear() {
  let idToken = null;
  try {
    const session = await cognitoAuth.getCurrentSession();
    idToken = (session && session.tokens && session.tokens.idToken) || null;
  } catch {
    // No readable SDK session (e.g. platform-SSO-only) — nothing to revoke via /auth/logout.
  }

  await Promise.allSettled([
    (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REVOKE_FETCH_TIMEOUT_MS);
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/auth/session`, {
          method: 'DELETE',
          credentials: 'include',
          signal: controller.signal,
        });
      } catch {
        // Best-effort cookie clear — logout proceeds regardless (CJIS §5.4).
      } finally {
        clearTimeout(timeoutId);
      }
    })(),
    idToken
      ? (async () => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), REVOKE_FETCH_TIMEOUT_MS);
          try {
            await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
              method: 'POST',
              signal: controller.signal,
              headers: { Authorization: `Bearer ${idToken}` },
            });
          } catch {
            // Best-effort audit/revoke — logout proceeds regardless (CJIS §5.4).
          } finally {
            clearTimeout(timeoutId);
          }
        })()
      : Promise.resolve(),
  ]);

  try {
    await cognitoAuth.signOut();
  } catch {
    /* ignore */
  }
}

// Handle an inbound message on this app's /logout-bridge. Extracted as a pure,
// directly-callable function (deps injectable) so it can be unit-tested without
// real iframe/postMessage plumbing. Returns true if the message was accepted.
export function handleBridgeMessage(event, options = {}) {
  const {
    allowedOrigins = getSiblingOrigins(),
    broadcast = broadcastLogout,
    revoke = serverRevokeAndClear,
  } = options;

  if (!isValidLogoutMessage(event, allowedOrigins)) return false;

  // 1. Tell already-open same-origin PC tabs to clear instantly.
  broadcast();
  // 2. Best-effort server revoke + local clear for this origin.
  revoke();
  return true;
}

// Fan out logout to sibling products: inject a hidden iframe per sibling origin
// pointing at its /logout-bridge, and wait for the bridge's ready ack before
// sending the logout payload — see the module header for why this replaced a
// `load`-based one-shot post. If the ready ack never arrives, a short retry
// interval keeps re-posting the logout payload for up to
// READY_RETRY_WINDOW_MS as a fallback. Each iframe (and its listener/interval)
// is torn down at IFRAME_LIFETIME_MS regardless of outcome. Fire-and-forget —
// callers must not await this.
export function fanOutCrossOriginLogout(doc = document, origins = getSiblingOrigins(), win = window) {
  origins.forEach((origin) => {
    try {
      const iframe = doc.createElement('iframe');
      iframe.style.display = 'none';
      iframe.setAttribute('aria-hidden', 'true');

      let settled = false;
      let intervalId = null;
      let elapsedMs = 0;

      const sendLogout = () => {
        try {
          iframe.contentWindow.postMessage(LOGOUT_MESSAGE, origin);
        } catch {
          /* sibling not reachable / blocked — best-effort */
        }
      };

      const stopRetrying = () => {
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };

      const onMessage = (event) => {
        if (settled) return;
        if (event.source !== iframe.contentWindow) return;
        if (event.origin !== origin) return;
        if (!event.data || event.data.source !== LOGOUT_MESSAGE_SOURCE || event.data.type !== 'ready') return;
        settled = true;
        sendLogout();
        stopRetrying();
        win.removeEventListener('message', onMessage);
      };
      win.addEventListener('message', onMessage);

      const teardown = () => {
        stopRetrying();
        win.removeEventListener('message', onMessage);
        try {
          iframe.remove();
        } catch {
          /* already gone */
        }
      };

      iframe.src = `${origin}${BRIDGE_PATH}`;
      doc.body.appendChild(iframe);

      intervalId = setInterval(() => {
        if (settled) {
          stopRetrying();
          return;
        }
        sendLogout();
        elapsedMs += READY_RETRY_INTERVAL_MS;
        if (elapsedMs >= READY_RETRY_WINDOW_MS) {
          stopRetrying();
        }
      }, READY_RETRY_INTERVAL_MS);

      setTimeout(teardown, IFRAME_LIFETIME_MS);
    } catch {
      /* never block logout on fan-out failure */
    }
  });
}
